import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import { fetchServiceDetail, fetchServices } from '../../api/serviceApi';
import MetaTagsUpdater from '../../components/MetaTagsUpdater'; // Import MetaTagsUpdater

// Helper function to unescape HTML entities
const unescapeHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.documentElement.textContent;
};

function ServiceDetail({ setDynamicSeo }) {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [otherServices, setOtherServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToast } = useToast();

  useEffect(() => {
    const fetchServiceDetails = async () => {
      if (!slug) {
        setLoading(false);
        setService(null);
        addToast('error', 'Invalid service URL. Slug is missing.');
        if (setDynamicSeo) {
          setDynamicSeo({ title: '', description: '', dynamicImage: '' });
        }
        return;
      }

      try {
        setLoading(true);

        const serviceResponse = await fetchServiceDetail(slug);
        if (!serviceResponse.success) {
          addToast('error', serviceResponse.message || 'Failed to fetch service details.');
          setLoading(false);
          setService(null);
          if (setDynamicSeo) {
            setDynamicSeo({ title: '', description: '', dynamicImage: '' });
          }
          return;
        }

        const fetchedService = serviceResponse.data;
        // Unescape HTML content for description
        const formattedService = {
          ...fetchedService,
          description: unescapeHtml(fetchedService.description),
        };
        setService(formattedService);

        if (setDynamicSeo) {
          setDynamicSeo({
            title: fetchedService.name,
            description: fetchedService.excerpt,
            dynamicImage: fetchedService.image, // Pass the image URL
          });
        }

        const recentServicesResponse = await fetchServices({ limit: 3, paginate: false });
        if (recentServicesResponse.success) {
          const filteredOther = recentServicesResponse.data
            .filter(s => s.slug !== fetchedService.slug)
            .slice(0, 3)
            .map(s => ({
              id: s.id,
              title: s.name,
              description: s.excerpt,
              slug: s.slug,
            }));
          setOtherServices(filteredOther);
        } else {
          setOtherServices([]);
        }

      } catch (e) {
        console.error("Error fetching service details:", e);
        addToast('error', 'Failed to load service details. Please try again later.');
        setService(null);
        if (setDynamicSeo) {
          setDynamicSeo({ title: '', description: '', dynamicImage: '' });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchServiceDetails();
  }, [slug, addToast, setDynamicSeo]);

  return (
    <div className="min-h-screen bg-bg-dark text-text-gray pt-20">
      <MetaTagsUpdater
        pageKey="ServiceDetail" // Use a specific pageKey for service details
        dynamicTitle={service?.name || ''}
        dynamicDescription={service?.excerpt || ''}
        dynamicImage={service?.image || ''} // Pass dynamicImage
      />
      <div className="container mx-auto px-4 py-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          {loading ? (
            <div className="bg-bg-light rounded-lg shadow-lg overflow-hidden p-8 mt-12 animate-pulse">
              <div className="w-full h-64 bg-gray-700 rounded-lg mb-8"></div>
              <div className="h-10 bg-gray-700 rounded w-3/4 mb-4"></div>
              <div className="h-4 bg-gray-700 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-700 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-700 rounded w-2/3"></div>
            </div>
          ) : !service ? (
            <div className="pt-20 text-center text-text-white min-h-screen bg-bg-dark">
              <h1 className="text-4xl font-bold mb-4">Service Not Found</h1>
              <p className="text-lg">The service you are looking for does not exist or the URL is invalid.</p>
              <Link to="/services" className="text-accent-red hover:underline mt-4 inline-block">
                Back to Services
              </Link>
            </div>
          ) : (
            <>
              {service.image && ( // Conditionally render image if it exists
                <img src={service.image} alt={service.name} className="w-full h-80 object-cover object-top rounded-lg mb-8 shadow-lg" />
              )}
              <h1 className="text-5xl font-bold text-text-white text-center mb-10 relative after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-20 after:h-1 after:bg-accent-red after:rounded-sm after:bottom-[-15px]">
                {service.name}
              </h1>

              <div className="bg-bg-light rounded-lg shadow-lg overflow-hidden p-8 mt-12">
                <div
                  className="rich-text-content" // Applied custom rich text styling class
                  dangerouslySetInnerHTML={{ __html: service.description }}
                ></div>
              </div>
            </>
          )}
        </div>

        <div className="lg:col-span-1">
          <div className="bg-gray-800 rounded-lg shadow-lg p-6 mb-8 border border-border-subtle">
            <h3 className="text-2xl font-bold text-text-white relative after:content-[''] after:absolute after:bottom-[-4px] after:left-0 after:w-16 after:h-1 after:bg-accent-red after:rounded-sm pb-1">Other Services</h3>
            {loading ? (
              <ul className="space-y-4 mt-4">
                {Array.from({ length: 3 }).map((_, index) => (
                  <li key={index} className="h-16 bg-gray-700 rounded mb-4 animate-pulse"></li>
                ))}
              </ul>
            ) : otherServices.length > 0 ? (
              <ul className="space-y-4 mt-4">
                {otherServices.map((otherService) => (
                  <li key={otherService.id} className="pb-4 border-b border-border-subtle last:border-b-0">
                    <Link to={`/services/${otherService.slug}`} className="block text-text-gray hover:text-accent-red transition-colors duration-200">
                      <h4 className="text-lg font-semibold">{otherService.title}</h4>
                      <p className="text-sm">{otherService.description}</p>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-text-gray text-sm">No other services found.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ServiceDetail;